import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps, Modal, Typography } from 'antd';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import OpenNotification from 'src/app/components/antd/OpenNotification';

import { BannerStatusEnum } from '../types/bannerManagement';
import { baseProps } from '../utils/modalUtils';

const { Text } = Typography;

interface BannerTableActionsProps {
  bannerId: number;
  status: BannerStatusEnum;
  onPublish: (bannerId: number) => Promise<void>;
  onDeactivate: (bannerId: number) => Promise<void>;
  onCancelSchedule: (bannerId: number) => Promise<void>;
  onDelete: (bannerId: number) => Promise<void>;
}

export const BannerTableActions: FC<BannerTableActionsProps> = ({
  bannerId,
  status,
  onPublish,
  onDeactivate,
  onCancelSchedule,
  onDelete,
}) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/marketplace/banners/create-or-update/${bannerId}`);
  };

  const handleClick: MenuProps['onClick'] = async event => {
    if (event.key === 'EDIT') {
      handleEdit();
      return;
    }

    if (event.key === 'PUBLISH') {
      Modal.confirm({
        ...baseProps,
        title: 'Ativar banner?',
        icon: <CheckCircleOutlined />,
        okText: 'Ativar',
        cancelText: 'Cancelar',
        content: (
          <Text>
            Tem certeza que deseja ativar este banner? Ele ficará visível para
            os usuários.
          </Text>
        ),
        onOk: async () => {
          await onPublish(bannerId);
          OpenNotification(false, 'Publicacao realizada com sucesso.');
        },
      });
      return;
    }

    if (event.key === 'DEACTIVATE') {
      Modal.confirm({
        ...baseProps,
        title: 'Desativar banner?',
        icon: <EyeInvisibleOutlined />,
        okText: 'Desativar',
        okButtonProps: { danger: true },
        cancelText: 'Cancelar',
        content: (
          <Text>
            Tem certeza que deseja desativar este banner? Ele deixará de ser
            exibido para os usuários.
          </Text>
        ),
        onOk: async () => {
          await onDeactivate(bannerId);
          OpenNotification(false, 'Banner desativado com sucesso.');
        },
      });
      return;
    }

    if (event.key === 'CANCEL_SCHEDULE') {
      await onCancelSchedule(bannerId);
      OpenNotification(false, 'Publicacao cancelada.');
      return;
    }

    if (event.key === 'DELETE') {
      Modal.confirm({
        title: 'Deletar banner?',
        icon: <DeleteOutlined />,
        centered: true,
        width: 650,
        okText: 'Excluir',
        okButtonProps: { danger: true },
        cancelText: 'Cancelar',
        content: (
          <Text>
            Tem certeza que deseja excluir este banner? Essa ação não poderá ser
            desfeita.
          </Text>
        ),
        onOk: async () => {
          await onDelete(bannerId);
          OpenNotification(false, 'Banner deletado com sucesso.');
        },
      });
    }
  };

  const items: MenuProps['items'] = useMemo(() => {
    if (
      status === BannerStatusEnum.Scheduled ||
      status === BannerStatusEnum.Active
    ) {
      return [
        {
          key: 'EDIT',
          label: 'Visualizar',
          icon: <EyeOutlined />,
        },
        {
          key: 'DEACTIVATE',
          label: 'Desativar',
          icon: <EyeInvisibleOutlined />,
          danger: true,
        },
      ];
    }

    if (status === BannerStatusEnum.Draft) {
      return [
        {
          key: 'EDIT',
          label: 'Editar',
          icon: <EditOutlined />,
        },
        {
          key: 'DELETE',
          label: 'Excluir',
          icon: <DeleteOutlined />,
          danger: true,
        },
      ];
    }

    return [
      {
        key: 'EDIT',
        label: 'Editar',
        icon: <EditOutlined />,
      },
      {
        key: 'PUBLISH',
        label: 'Publicar',
        icon: <CheckCircleOutlined />,
      },
      {
        key: 'DELETE',
        label: 'Excluir',
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];
  }, [status]);

  return (
    <Dropdown
      placement='bottomRight'
      menu={{ items, onClick: handleClick }}
      trigger={['click']}
    >
      <ActionButton style={{ border: 'none' }}>
        <MoreOutlined />
      </ActionButton>
    </Dropdown>
  );
};

const ActionButton = styled(Button)`
  &:hover {
    background-color: rgba(62, 62, 62, 0.7);
    color: white;
  }
`;
